import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, Utensils, Shirt, BookOpen, Sprout, ArrowRight } from 'lucide-react';

const programs = [
  {
    icon: Utensils,
    title: 'Food Distribution',
    description: 'We provide nutritious food to families in need through our regular food drives and partnerships with local organizations.',
    color: 'gold',
    comingSoon: false
  },
  {
    icon: Shirt,
    title: 'Clothing Ministry',
    description: 'Gently used and new clothing items are collected and distributed to those who need them most.',
    color: 'gold',
    comingSoon: true
  },
  {
    icon: Users,
    title: 'Youth Mentorship',
    description: 'Our mentorship program pairs young people with caring adults who guide them in faith and life skills.',
    color: 'deep-red',
    comingSoon: true
  },
  {
    icon: BookOpen,
    title: 'Adult Education',
    description: 'We offer educational resources and support to help adults improve their skills and opportunities.',
    color: 'deep-red',
    comingSoon: true
  }
];

export const Outreach = () => {
  return (
    <div data-testid="outreach-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1758270703397-52d481a67409?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Making a Difference</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Community <span className="text-gold">Outreach</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              As a 501(c)(3) certified nonprofit, we're committed to transforming our community through love, service, and practical support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-dark gold-glow text-center"
          >
            <Heart className="text-gold mx-auto mb-4" size={48} />
            <h2 className="font-cinzel text-2xl text-foreground mb-4">Our Calling</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              "For I was hungry and you gave me something to eat, I was thirsty and you gave me something to drink, I was a stranger and you invited me in." — Matthew 25:35
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-cinzel text-3xl text-gold mb-4">Our Programs</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every program we run is designed to meet real needs and show God's love in practical ways.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark group relative"
                data-testid={`program-${index}`}
              >
                {program.comingSoon && (
                  <div className="absolute -top-3 -right-3 bg-gold text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10">
                    Coming Soon
                  </div>
                )}
                <div className={`flex gap-6 ${program.comingSoon ? 'opacity-70' : ''}`}>
                  <div className={`w-16 h-16 flex-shrink-0 rounded-full flex items-center justify-center transition-colors ${
                    program.color === 'gold' 
                      ? 'bg-gold/10 group-hover:bg-gold/20' 
                      : 'bg-deep-red/10 group-hover:bg-deep-red/20'
                  }`}>
                    <program.icon className={program.color === 'gold' ? 'text-gold' : 'text-deep-red'} size={32} />
                  </div>
                  <div>
                    <h3 className="font-cinzel text-xl text-foreground mb-3">{program.title}</h3>
                    <p className="text-muted-foreground">{program.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/n6h10t5c_Lion%20King%202.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm mb-6">
              <Sprout size={16} />
              <span>Future Vision</span>
            </div>
            <h2 className="font-cinzel text-3xl sm:text-4xl text-foreground mb-6">
              Growing Our <span className="text-gold">Impact</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              We're working toward purchasing land to grow organic crops and raise organic livestock. This will allow us to provide healthier food options to our community while teaching sustainable farming practices.
            </p>
            <Link to="/donate" className="btn-primary inline-flex items-center gap-2">
              Support This Vision
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-cinzel text-3xl text-gold mb-6">Get Involved</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  There are many ways to be part of our outreach ministry. Whether you can give your time, talents, or resources, every contribution makes a difference.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gold mt-2" />
                    <span><strong className="text-foreground">Volunteer:</strong> Join us at our food and clothing distribution events</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gold mt-2" />
                    <span><strong className="text-foreground">Donate:</strong> Financial contributions help us expand our reach</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gold mt-2" />
                    <span><strong className="text-foreground">Mentor:</strong> Become a mentor for our youth program</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gold mt-2" />
                    <span><strong className="text-foreground">Spread the Word:</strong> Share our mission with others</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/contact" className="btn-primary">
                  Contact Us
                </Link>
                <Link to="/donate" className="btn-secondary">
                  Make a Donation
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-sm overflow-hidden border border-border">
                <img
                  src="https://images.unsplash.com/photo-1599059813005-11265ba4b4ce?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Volunteers serving the community"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gold text-black p-6 rounded-sm">
                <p className="font-cinzel text-3xl font-bold">501(c)(3)</p>
                <p className="text-sm">Certified Nonprofit</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
